/*
 * Copyright 2017-2017 Amazon.com, Inc. or its affiliates. All Rights Reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License"). You may not use this file except in compliance with
 * the License. A copy of the License is located at
 *
 *     http://aws.amazon.com/apache2.0/
 *
 * or in the "license" file accompanying this file. This file is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR
 * CONDITIONS OF ANY KIND, either express or implied. See the License for the specific language governing permissions
 * and limitations under the License.
 */

import * as React from 'react';
import { I18n, JS, ConsoleLogger as Logger, Hub } from '@aws-amplify/core';
import Auth from '@aws-amplify/auth';

import { AuthPiece } from 'aws-amplify-react';

import { PageLoader, IconButton } from '@kambria/kambria-lego';

import {
  FormSection,
  FormField,
  SectionHeader,
  SectionBody,
  SectionFooter,
  Button,
  Link,
  Hint,
  Input,
  InputLabel,
  SectionFooterPrimaryContent,
  SectionFooterSecondaryContent,
} from 'aws-amplify-react/lib/Amplify-UI/Amplify-UI-Components-React';

const logger = new Logger('SignIn');

class SignIn extends AuthPiece {
  constructor(props) {
    super(props);

    this._validAuthStates = ['signIn', 'signedOut', 'signedUp'];
    this.state = {
      loading: false
    };

    // Store path to return back if using facebook sign in
    if (window.location.pathname !== '/auth') {
      localStorage.setItem('pathBeforeSignIn', window.location.pathname + window.location.search);
    }
  }

  componentDidMount() {
    window.addEventListener('keydown', this.onKeyDown);
  }

  componentWillUnmount() {
    window.removeEventListener('keydown', this.onKeyDown);
  }

  onKeyDown = (e) => {
    if (this.props.authState === 'signIn') {
      if (e.keyCode === 13) { // when press enter
        this.signIn();
      }
    }
  }

  checkContact = (user) => {
    if (!Auth || typeof Auth.verifiedContact !== 'function') {
      throw new Error('No Auth module found, please ensure @aws-amplify/auth is imported');
    }
    Auth.verifiedContact(user)
      .then(data => {
        if (!JS.isEmpty(data.verified)) {
          this.changeState('signedIn', user);
        } else {
          user = Object.assign(user, data);
          this.changeState('verifyContact', user);
        }
      });
  }

  hideLoading = () => {
    this.setState({ loading: false });
  }

  signIn = () => {
    this.setState({ loading: true });

    const { username = '', password = '' } = this.inputs || {};
    if (!Auth || typeof Auth.signIn !== 'function') {
      throw new Error('No Auth module found, please ensure @aws-amplify/auth is imported');
    }
    Auth.signIn(username, password)
      .then(user => {
        this.hideLoading();

        if (user) {
          if (user.challengeName === 'SMS_MFA' || user.challengeName === 'SOFTWARE_TOKEN_MFA') {
            logger.debug('confirm user with ' + user.challengeName);
            this.changeState('confirmSignIn', user);
          } else if (user.challengeName === 'NEW_PASSWORD_REQUIRED') {
            logger.debug('require new password', user.challengeParam);
            this.changeState('requireNewPassword', user);
          } else if (user.challengeName === 'MFA_SETUP') {
            logger.debug('TOTP setup', user.challengeParam);
            this.changeState('TOTPSetup', user);
          }
          else {
            this.checkContact(user);
          }
        } else {
          this.error(new Error('Get user information failed'));
        }
      })
      .catch(err => {
        this.hideLoading();

        if (err.code === 'UserNotConfirmedException') {
          logger.debug('the user is not confirmed');
          this.changeState('confirmSignUp', username);
        }
        else if (err.code === 'PasswordResetRequiredException') {
          logger.debug('the user requires a new password');
          this.changeState('requireNewPassword');
        } else {
          this.error(err);
        }
      });
  }

  onSignInFacebookClick = () => {
    try {
      const scope = 'public_profile,email';

      this.setState({ loading: true });

      this.loginFacebook(scope);
    } catch (err) {
      this.hideLoading();

      this.error(new Error(err.message));
    }
  }

  loginFacebook = (scope) => {
    if (!window.FB && navigator.doNotTrack === '1') {
      const message = 'The Content Blocking settings on this browser are preventing you from using the “Login with Facebook” feature. Please change the settings or use the email login!';
      this.error(new Error(message));
      return this.hideLoading();
    }

    window.FB.login(response => {
      if (!response || !response.authResponse) {
        return this.hideLoading();
      }
      this.federatedSignIn(response.authResponse);
    }, {
        scope: scope,
        auth_type: 'rerequest'
      });
  }

  federatedSignIn = response => {
    const { accessToken } = response;

    if (!accessToken) {
      return this.hideLoading();
    }

    window.FB.api('/me', { fields: 'email' }, response => {
      if (response.email) {
        const user = {
          accessToken: accessToken,
          provider: 'Facebook'
        };

        Hub.dispatch('auth', {
          event: 'federatedSignIn',
          data: user
        });
      } else {
        this.error(new Error('Can not access email address'));
        Hub.dispatch('auth', {
          event: 'federatedSignInFailure',
          data: {
            message: 'Can not access email address'
          }
        });
        return this.hideLoading();
      }
    });
  }

  signInGoogle = () => {
    const ga = window.gapi.auth2.getAuthInstance();

    ga.signIn().then(async googleUser => {
      const profile = googleUser.getBasicProfile(),
        user = {
          userId: profile.getId(),
          email: profile.getEmail(),
          name: profile.getName(),
          picture: profile.getImageUrl(),
          accessToken: googleUser.getAuthResponse().id_token,
          provider: 'Google'
        };

      Hub.dispatch('auth', {
        event: 'federatedSignIn',
        data: user
      });
    },
      error => {
        this.error(new Error(error.error));
        Hub.dispatch('auth', {
          event: 'federatedSignInFailure',
          data: {
            message: error
          }
        });
      });
  }

  showComponent = (theme) => {
    const { hide = [] } = this.props;
    if (hide && hide.includes(SignIn)) { return null; }

    return (
      <FormSection theme={theme}>
        <SectionHeader theme={theme}>{I18n.get('Sign in to proceed')}</SectionHeader>
        <SectionBody theme={theme}>
          <div className="mb-4">
            <IconButton
              type="fb"
              size="sm"
              className="mr-4"
              customStyle={{ width: '100%' }}
              onClick={this.onSignInFacebookClick}
            >
              Continue with Facebook
            </IconButton>
          </div>
          <p style={{
            width: '100%',
            textAlign: 'center',
            borderBottom: '1px solid #c8c8c8',
            lineHeight: '0.1em',
            margin: '40px 0 37px',
          }}>
            <span style={{
              background: '#fff',
              padding: '0 10px',
              color: '#8a8a8a'
            }}>or</span>
          </p>
          <FormField theme={theme}>
            <InputLabel>{I18n.get('Email')} *</InputLabel>
            <Input
              autoFocus
              placeholder={I18n.get('Enter your email')}
              theme={theme}
              key="username"
              name="username"
              onChange={this.handleInputChange}
            />
          </FormField>
          <FormField theme={theme}>
            <InputLabel>{I18n.get('Password')} *</InputLabel>
            <Input
              placeholder={I18n.get('Enter your password')}
              theme={theme}
              key="password"
              type="password"
              name="password"
              onChange={this.handleInputChange}
            />
            <Hint theme={theme}>
              {I18n.get('Forgot your password? ')}
              <Link theme={theme} onClick={() => this.changeState('forgotPassword')}>
                {I18n.get('Reset password')}
              </Link>
            </Hint>
          </FormField>

        </SectionBody>
        <SectionFooter theme={theme}>
          <SectionFooterPrimaryContent theme={theme}>
            <Button theme={theme} onClick={this.signIn}>
              {I18n.get('Sign In')}
            </Button>
          </SectionFooterPrimaryContent>
          {
            <SectionFooterSecondaryContent theme={theme}>
              {I18n.get('No account? ')}
              <Link theme={theme} onClick={() => this.changeState('signUp')}>
                {I18n.get('Create account')}
              </Link>
            </SectionFooterSecondaryContent>
          }
        </SectionFooter>
        {this.state.loading &&
          <div style={{
            position: 'fixed',
            width: '100%',
            height: '100%',
            top: 0,
            background: '#0000004d',
            left: 0,
          }}>
            <PageLoader type='center' />
          </div>
        }
      </FormSection>
    );
  }
}

export default SignIn;